/* eslint-disable react-hooks/exhaustive-deps */
import type { CreditCardData, LegacyCartFormMessage } from 'cart-ui-react';
import { CreditCardFields, CreditCardForm, getCreditCardData, useCreditCardFieldsState } from 'cart-ui-react';
import { Button } from 'forms';
import t from 'rcm-i18n';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectState, selectCartInfo, selectCartPaymentInfo, selectIsEditing } from 'cart-ui-react/src/autoshipState/selectors';
import CartV2CreditCardFields from '/cart-ui-react/src/container/CartV2/components/CartV2CreditCardFields';
import { getCheckoutOrderManager } from 'cart-ui';
import { setEditingAlert, setSnackbar, setWorldPay3dsData, setError, setDisableSubmitOrder, setCartPaymentInfo } from 'cart-ui-react/src/autoshipState/autoshipSlice';
import axios from 'axios';

const errorLoggingClientAuthCode =
'zWufneVAd9FeUTco3FJ5mcZMWp9zJ1dQyB3zvEsnkzr8huRXJEXcSBnIm5GEvS9T';

export const getLogData = (cartV2Info, additionalParams = {}) => {
  const data: any = {
    cartPaymentInfo: {
      ...cartV2Info.cartPaymentInfo,
      cardNumber: `REDACTED${cartV2Info.cartPaymentInfo.cardNumber.slice(-4)}`,
      verificationCde: 'REDACTED',
    },
    ...additionalParams,
  };
  return data;
};

// this component fires the API call to Vista for the initial payment request
// when we hear back from the iframe with the sessionId
const DdcListener = ({ setShowIframe, cartV2PaymentInfo, dispatch, cartV2Info, paymentType }) => {
  const cart = useSelector(selectState);
  useEffect(() => {
    function handleMessage(event) {
      axios.get('/api/v2/cardinal/config')
        .then((res) => {
          const config = res.data.config;
          if (event.origin === config.ddcUrl) {
            axios.post('/chase3ds/logFromBrowser', {
              action: 'wp_ddcListenerFired',
              authorization: errorLoggingClientAuthCode,
              message: 'Received response from DDC',
              details: getLogData(cart),
            });
            try {
              const data = JSON.parse(event.data);

              if (data && data.Payload.ActionCode === 'SUCCESS') {
                // sessionId later becomes dfReferenceId in the flow
                const sessionId = data.Payload.SessionId;

                const browserInfo = {
                  dfReferenceId: sessionId,
                  acceptHeader: "text/html", // Static, usually safe to assume
                  userAgentHeader: navigator.userAgent,
                  browserJavaEnabled: navigator.javaEnabled(),
                  browserLanguage: navigator.language ?? 'en-us',
                  browserColourDepth: screen.colorDepth,
                  browserScreenHeight: screen.height,
                  browserScreenWidth: screen.width,
                  browserJavaScriptEnabled: true, // Static, JS must be enabled for this to run
                  timeZone: -new Date().getTimezoneOffset(), // in minutes, convert as needed
                };

                dispatch(setWorldPay3dsData(browserInfo));
                getCheckoutOrderManager().then(async (orderManager) => {
                  const payment = {
                    cardNumber: cartV2PaymentInfo.cardNumber,
                    nameOnCard: cartV2PaymentInfo.nameOnCard,
                    expirationMonth: Number(cartV2PaymentInfo.expirationMonth),
                    expirationYear: Number(cartV2PaymentInfo.expirationYear),
                    verificationCode: cartV2PaymentInfo.verificationCode,
                    worldpay3dsData: browserInfo,
                  };
                  axios.post('/chase3ds/logFromBrowser', {
                    action: 'wp_ddcSuccess',
                    authorization: errorLoggingClientAuthCode,
                    message: 'DDC step successful, processing order',
                    details: getLogData(cart, browserInfo),
                  });
                  // check for cart changes (mainly Kalogris donations but any frontend cart changes are applied here)
                  await orderManager.patchOrder(cartV2Info);
                  await orderManager.processCurrentOrder({ type: paymentType, data: payment }).then(() => {
                    const paymentError = JSON.parse(localStorage.getItem('paymentError'));
                    if (paymentError) {
                      setShowIframe(false);
                      dispatch(setError(paymentError[0].value));
                      localStorage.removeItem('paymentError');
                      window.rcmLoading.setLoading('PaymentFormCreditCardWorldPay3ds', 1);
                      axios.post('/chase3ds/logFromBrowser', {
                        action: 'wp_processOrderFailed',
                        authorization: errorLoggingClientAuthCode,
                        message: 'processCurrentOrder() failed - ' + paymentError[0].value,
                        details: getLogData(cart, browserInfo),
                      });
                    } else {
                      axios.post('/chase3ds/logFromBrowser', {
                        action: 'wp_processOrderSuccess',
                        authorization: errorLoggingClientAuthCode,
                        message: 'processCurrentOrder() successful',
                        details: getLogData(cart, browserInfo),
                      });
                    }
                  });
                });
              }
            } catch (err) {
              console.error('Error parsing message from iframe:', err);
            }
          }
        })
        .catch(err => {
          axios.post('/chase3ds/logFromBrowser', {
            action: 'wp_ddcListenerError',
            authorization: errorLoggingClientAuthCode,
            message: 'Error from DDC response - ' + err.response?.data || err.message,
            details: getLogData(cart),
          });
          console.error('Error:', err.response?.data || err.message);
        })

    }

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [cartV2PaymentInfo]);

  return null; // This component doesn't render anything
};

// this iframe will use the JWT built in the handleSubmit, and the cardNumber, to send to WorldPay for analysis
const WorldPayIframeSubmit = ({ bin, dispatch, iframeRef, worldpayConfig, setWorldpayConfig, cartV2Info }) => {
  const cart = useSelector(selectState);

  const fireIframe = (att = 0) => {
    if (att >= 3) {
      window.rcmLoading.setLoading('PaymentFormCreditCardWorldPay3ds', 1);
      dispatch(setDisableSubmitOrder(true));
      dispatch(setError('Sorry, we are having trouble processing your order.'));
      return;
    }

    const iframe = iframeRef.current;
    if (!iframe) return;

    const doc = iframe.contentWindow?.document;
    if (!doc) return;
    axios
      .get('/api/v2/cardinal/jwt/ddc')
      .then((response: any) => {
        if (response.error) {
          console.log('API error: ' + response.error.toString());
          return null;
        } else {
          return response.data.jwt;
        }
      })
      .then((jwt) => {
        if (!jwt) return; // Handle null case
        if (worldpayConfig.length === undefined || !worldpayConfig) {
          axios
            .get('/api/v2/cardinal/config')
            .then(res => {
              const config = res.data.config;
              setWorldpayConfig(config);
              const html = `
              <form id="collectForm" method="POST" action="${config.ddcIframeUrl}">
                <input type="hidden" name="Bin" value="${bin}" />
                <input type="hidden" name="JWT" value="${jwt}" />
              </form>
              <script>
                window.onload = function() {
                  document.getElementById('collectForm').submit();
                };
              </script>
            `;
              axios.post('/chase3ds/logFromBrowser', {
                action: 'wp_ddcIframeSubmit',
                authorization: errorLoggingClientAuthCode,
                message: 'DDC iframe form submit',
                details: getLogData(cart, {
                  bin: bin,
                  jwt: jwt,
                  returnUrl: config.returnUrl,
                }),
              });
              iframeRef.current.srcdoc = html;
            })
            .catch(err => {
              console.error('Error:', err.response?.data || err.message);
            });
        } else {
          const html = `
            <form id="collectForm" method="POST" action="${worldpayConfig.ddcIframeUrl}">
              <input type="hidden" name="Bin" value="${bin}" />
              <input type="hidden" name="JWT" value="${jwt}" />
            </form>
            <script>
              window.onload = function() {
                document.getElementById('collectForm').submit();
              };
            </script>
          `;
          iframeRef.current.srcdoc = html;
          axios.post('/chase3ds/logFromBrowser', {
            action: 'wp_ddcIframeSubmit',
            authorization: errorLoggingClientAuthCode,
            message: 'DDC iframe form submit',
            details: getLogData(cart, {
              bin: bin,
              jwt: jwt,
            }),
          });
        }
      })
      .catch((error) => {
        console.error(`Retry attempt ${att + 1}:`, error.response?.data || error.message);
        setTimeout(() => fireIframe(att + 1), 1500);
      });
  };

  useEffect(() => {
    fireIframe();
  }, [bin]);

  return <iframe ref={iframeRef} title="WorldPay DDC" width="1" height="1" style={{ display: 'none' }} />;
};

interface Props {
  order: any;
  onSubmit?: (data: { type: string; data: CreditCardData }) => void;
  messages: LegacyCartFormMessage[];
  paymentType: string;
  isCartV2?: boolean;
  disabled?: boolean;
  setError?: any;
}

const PaymentFormCreditCardWorldPay3ds = ({
  order,
  messages,
  paymentType,
  isCartV2 = false,
  disabled = false,
  setError,
}: Props) => {
  const creditCardFieldsState = useCreditCardFieldsState();
  const dispatch = useDispatch();
  const [errorMessage, setErrorMessage] = useState('');
  const cartV2PaymentInfo = useSelector(selectCartPaymentInfo);
  const isEditing = useSelector(selectIsEditing);
  const [showIframe, setShowIframe] = useState(false);
  const [worldpayConfig, setWorldpayConfig] = useState({});
  const cartV2Info = useSelector(selectCartInfo);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [cannotSubmit, setCannotSubmit] = useState(false);
  const timeoutRef = useRef<any>(null);
  const cart = useSelector(selectState);

  const handleEditingAlert = () => {
    if (isCartV2 && isEditing) {
      dispatch(setEditingAlert(true));
      dispatch(
        setSnackbar({
          message: t('Please save any changes before you proceed.'),
          type: 'warning',
          delay: 4500,
        })
      );
      setTimeout(() => {
        dispatch(setEditingAlert(false));
      }, 100);
      return;
    }
  };

  // keeping in case we need to revert from the smart-submit behavior
  // const handleSubmit = useCallback(
  //   (event) => {
  //     event.preventDefault();
  //     setErrorMessage('');
  //     window.rcmLoading.setLoading('PaymentFormCreditCardWorldPay3ds', 0);
  //     setShowIframe(true);
  //   },
  //   [cartV2PaymentInfo]
  // );

  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault();
      setErrorMessage('');
      axios.post('/chase3ds/logFromBrowser', {
        action: 'wp_handleSubmitCalled',
        authorization: errorLoggingClientAuthCode,
        message: 'Initial Submit triggered',
        details: getLogData(cart),
      });

      if (cannotSubmit) {
        dispatch(setError(t('Please wait a few moments before trying to submit again.')));
        return;
      }

      const now = Date.now();
      localStorage.setItem('worldpay3ds_lastSubmit', now.toString());
      setCannotSubmit(true);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        setCannotSubmit(false);
        localStorage.removeItem('worldpay3ds_lastSubmit');
      }, 120000); // 3 minutes

      window.rcmLoading.setLoading('PaymentFormCreditCardWorldPay3ds', 0);
      setShowIframe(true);
    },
    [cartV2PaymentInfo, cannotSubmit]
  );

  useEffect(() => {
    const lastSubmitTime = localStorage.getItem('worldpay3ds_lastSubmit');
    if (lastSubmitTime) {
      const elapsed = Date.now() - parseInt(lastSubmitTime, 10);
      if (elapsed < 120000) {
        setCannotSubmit(true);
        timeoutRef.current = setTimeout(() => {
          setCannotSubmit(false);
          localStorage.removeItem('worldpay3ds_lastSubmit');
        }, 120000 - elapsed);
      }
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="WorldPayPaymentFormWrapper">
      {!!errorMessage && <div className="alert alert-warning">{t(errorMessage)}</div>}
      <CreditCardForm onSubmit={handleSubmit} cartV2={isCartV2}>
        <CreditCardForm.Body>
          {isCartV2 ? (
            <CartV2CreditCardFields />
          ) : (
            <CreditCardFields {...creditCardFieldsState} disabled={disabled} messages={messages} order={order} />
          )}
        </CreditCardForm.Body>
        <CreditCardForm.Foot>
          {disabled || isEditing ? (
            <Button
              css={{ opacity: '0.7' }}
              btn="primary"
              className="continueButton"
              onClick={handleEditingAlert}
              text={t('Place Order')}
            />
          ) : (
            <div>
              <Button
                btn="primary"
                className="continueButton"
                disabled={disabled || isEditing}
                text={t('Place Order')}
                type="submit"
              />
            </div>
          )}
        </CreditCardForm.Foot>
      </CreditCardForm>
      {showIframe && (
        <WorldPayIframeSubmit
          cartV2Info={cartV2Info}
          worldpayConfig={worldpayConfig}
          setWorldpayConfig={setWorldpayConfig}
          iframeRef={iframeRef}
          bin={cartV2PaymentInfo.cardNumber.substring(0, 6)}
          dispatch={dispatch}
        />
      )}
      <DdcListener
        setShowIframe={setShowIframe}
        cartV2PaymentInfo={cartV2PaymentInfo}
        paymentType={paymentType}
        cartV2Info={cartV2Info}
        dispatch={dispatch}
      />
    </div>
  );
};

export default PaymentFormCreditCardWorldPay3ds;
